import { isMeetingTabId, type MeetingTabId } from "@/components/meetings/meetingTypeRegistry";
import type { AppLocationCodec } from "@/navigation/appLocation";
import { NAV_KEYS } from "@/navigation/keys";

export type MeetingsBriefingView = "briefing";
export type MeetingsLocationView = MeetingsBriefingView;

export type MeetingApprovalView =
  | "transcript-approval"
  | "extractions-approval"
  | "crm-updates";

export type MeetingsCalendarStatus = "connected" | "error";

export type MeetingsLocation = {
  meetingId?: string;
  view?: MeetingsLocationView;
  focus?: string;
  /** Window-only reopen signal; never serialized to URLs. */
  navSeq?: string;
  calendar?: MeetingsCalendarStatus;
};

export type MeetingFullLocation = {
  meetingId: string;
  tab?: MeetingTabId;
  view?: "full" | MeetingApprovalView;
  taskId?: string;
  clientName?: string;
};

const MEETINGS_RESERVED_PATH_SEGMENTS = new Set(["calendar"]);

export const MEETING_APPROVAL_PATH_SUFFIXES: ReadonlySet<string> = new Set([
  "transcript-approval",
  "extractions-approval",
  "crm-updates",
]);

function readString(params: Record<string, string | undefined>, key: string): string | undefined {
  return params[key]?.trim() || undefined;
}

export function parseMeetingIdFromPath(pathname: string): string | undefined {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "meetings" || !segments[1]) {
    return undefined;
  }
  const candidate = decodeURIComponent(segments[1]);
  if (MEETINGS_RESERVED_PATH_SEGMENTS.has(candidate)) {
    return undefined;
  }
  return candidate;
}

function parseMeetingApprovalViewFromPath(pathname: string): MeetingApprovalView | undefined {
  const segments = pathname.split("/").filter(Boolean);
  const suffix = segments[2];
  if (suffix && MEETING_APPROVAL_PATH_SUFFIXES.has(suffix)) {
    return suffix as MeetingApprovalView;
  }
  return undefined;
}

function isMeetingApprovalView(raw: string | undefined): raw is MeetingApprovalView {
  return raw != null && MEETING_APPROVAL_PATH_SUFFIXES.has(raw);
}

export function normalizeMeetings(loc: MeetingsLocation): MeetingsLocation {
  if (loc.meetingId) {
    const next: MeetingsLocation = { meetingId: loc.meetingId };
    if (loc.view === "briefing") {
      next.view = "briefing";
    }
    if (loc.navSeq) {
      next.navSeq = loc.navSeq;
    }
    return next;
  }
  const next: MeetingsLocation = {};
  if (loc.focus) next.focus = loc.focus;
  if (loc.navSeq) next.navSeq = loc.navSeq;
  if (loc.calendar) next.calendar = loc.calendar;
  return next;
}

function readBriefingView(params: Record<string, string | undefined>): MeetingsBriefingView | undefined {
  if (params.tab === "briefing") {
    return "briefing";
  }
  const raw = readString(params, NAV_KEYS.view);
  if (raw === "briefing") {
    return "briefing";
  }
  return undefined;
}

function readCalendarStatus(
  params: Record<string, string | undefined>,
): MeetingsCalendarStatus | undefined {
  const raw = readString(params, "calendar");
  if (raw === "connected" || raw === "error") {
    return raw;
  }
  return undefined;
}

function meetingsFromParams(params: Record<string, string | undefined>): MeetingsLocation {
  const base: MeetingsLocation = {};
  const meetingId = readString(params, "meetingId");
  if (meetingId) {
    base.meetingId = meetingId;
    const view = readBriefingView(params);
    if (view) {
      base.view = view;
    }
  } else {
    const focus = readString(params, NAV_KEYS.focus);
    if (focus) base.focus = focus;
    const calendar = readCalendarStatus(params);
    if (calendar) base.calendar = calendar;
  }
  const navSeq = readString(params, "navSeq");
  if (navSeq) base.navSeq = navSeq;
  return normalizeMeetings(base);
}

function meetingsFromUrl(pathname: string, searchParams: URLSearchParams): MeetingsLocation {
  const params: Record<string, string | undefined> = Object.fromEntries(searchParams);
  delete params.navSeq;
  const pathMeetingId = parseMeetingIdFromPath(pathname);
  if (pathMeetingId) {
    params.meetingId = pathMeetingId;
  }
  return meetingsFromParams(params);
}

function meetingsToParams(loc: MeetingsLocation): Record<string, string> {
  const n = normalizeMeetings(loc);
  const out: Record<string, string> = {};
  if (n.meetingId) {
    out.meetingId = n.meetingId;
    if (n.view === "briefing") {
      out.tab = "briefing";
    }
  } else {
    if (n.focus) out[NAV_KEYS.focus] = n.focus;
    if (n.calendar) out.calendar = n.calendar;
  }
  if (n.navSeq) {
    out.navSeq = n.navSeq;
  }
  return out;
}

function meetingsToUrl(loc: MeetingsLocation): string {
  const n = normalizeMeetings(loc);
  if (n.meetingId) {
    const path = `/meetings/${encodeURIComponent(n.meetingId)}`;
    if (n.view === "briefing") {
      return `${path}?${NAV_KEYS.view}=briefing`;
    }
    return path;
  }
  const query = meetingsToParams(n);
  delete query.meetingId;
  delete query.tab;
  delete query.navSeq;
  const qs = new URLSearchParams(query).toString();
  return `/meetings${qs ? `?${qs}` : ""}`;
}

export const meetingsCodec: AppLocationCodec<MeetingsLocation> = {
  appId: "meetings",
  fromUrl: meetingsFromUrl,
  fromParams: meetingsFromParams,
  toParams: meetingsToParams,
  toUrl: meetingsToUrl,
};

function readMeetingFullTab(
  params: Record<string, string | undefined>,
): MeetingTabId | undefined {
  const raw = readString(params, "tab");
  return isMeetingTabId(raw) ? raw : undefined;
}

function meetingFullFromParams(params: Record<string, string | undefined>): MeetingFullLocation {
  const meetingId = readString(params, "meetingId") ?? "";
  const tab = readMeetingFullTab(params);
  const viewRaw = readString(params, NAV_KEYS.view);
  const view =
    viewRaw === "full" || isMeetingApprovalView(viewRaw) ? viewRaw : undefined;
  const taskId = readString(params, NAV_KEYS.taskId);
  const clientName = readString(params, "clientName");
  const base: MeetingFullLocation = { meetingId };
  if (tab) base.tab = tab;
  if (view) base.view = view;
  if (taskId) base.taskId = taskId;
  if (clientName) base.clientName = clientName;
  return base;
}

function meetingFullFromUrl(pathname: string, searchParams: URLSearchParams): MeetingFullLocation {
  const params: Record<string, string | undefined> = Object.fromEntries(searchParams);
  delete params.navSeq;
  const pathMeetingId = parseMeetingIdFromPath(pathname);
  if (pathMeetingId) {
    params.meetingId = pathMeetingId;
  }
  const pathApprovalView = parseMeetingApprovalViewFromPath(pathname);
  if (pathApprovalView) {
    params[NAV_KEYS.view] = pathApprovalView;
  }
  return meetingFullFromParams(params);
}

function meetingFullToParams(loc: MeetingFullLocation): Record<string, string> {
  const meetingId = loc.meetingId?.trim();
  if (!meetingId) {
    return {};
  }
  const out: Record<string, string> = { meetingId };
  if (loc.tab) {
    out.tab = loc.tab;
  }
  if (loc.view) {
    out[NAV_KEYS.view] = loc.view;
  }
  if (loc.taskId) {
    out[NAV_KEYS.taskId] = loc.taskId;
  }
  if (loc.clientName) {
    out.clientName = loc.clientName;
  }
  return out;
}

function meetingFullToUrl(loc: MeetingFullLocation): string {
  const meetingId = loc.meetingId?.trim();
  if (!meetingId) {
    return "/meetings";
  }
  if (loc.view && isMeetingApprovalView(loc.view)) {
    const path = `/meetings/${encodeURIComponent(meetingId)}/${loc.view}`;
    const query = new URLSearchParams();
    if (loc.taskId) query.set(NAV_KEYS.taskId, loc.taskId);
    if (loc.clientName) query.set("clientName", loc.clientName);
    const qs = query.toString();
    return qs ? `${path}?${qs}` : path;
  }
  const query = new URLSearchParams({ [NAV_KEYS.view]: loc.view ?? "full" });
  if (loc.tab) {
    query.set("tab", loc.tab);
  }
  if (loc.taskId) query.set(NAV_KEYS.taskId, loc.taskId);
  if (loc.clientName) query.set("clientName", loc.clientName);
  return `/meetings/${encodeURIComponent(meetingId)}?${query.toString()}`;
}

export const meetingCodec: AppLocationCodec<MeetingFullLocation> = {
  appId: "meeting",
  fromUrl: meetingFullFromUrl,
  fromParams: meetingFullFromParams,
  toParams: meetingFullToParams,
  toUrl: meetingFullToUrl,
};

export function isMeetingApprovalSurface(
  view: string | undefined,
): view is MeetingApprovalView {
  return isMeetingApprovalView(view);
}
